import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import TutorialVideo from "@/components/TutorialVideo";
import { Video, Play, X } from "lucide-react";

/**
 * Reusable contextual tutorial helper.
 * Usage:
 *   <TutorialHelp category="ideas" />        // shows all tutorials in that category
 *   <TutorialHelp tutorialKey="payments" />  // shows a specific tutorial as a popover trigger
 *   <TutorialHelp compact />                 // single button that opens a modal grid of all tutorials
 */
export default function TutorialHelp({ category, tutorialKey, compact = false, title = "Help & Tutorials" }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const params = category ? { category } : {};
    api.get("/tutorials", { params }).then((r) => {
      let list = r.data;
      if (tutorialKey) list = list.filter((t) => t.key === tutorialKey);
      setItems(list);
    }).catch(() => {});
  }, [category, tutorialKey]);

  if (items.length === 0) return null;

  // Single tutorial → inline compact card with "Watch" button
  if (tutorialKey && items.length === 1) {
    const t = items[0];
    return (
      <>
        <button onClick={() => setSelected(t)} className="w-full text-left border border-slate-200 rounded-sm p-4 flex items-center gap-3 hover:border-kb-primary hover:bg-kb-subtle transition" data-testid={`tutorial-help-${t.key}`}>
          <div className="w-10 h-10 bg-kb-primary/10 rounded-sm flex items-center justify-center shrink-0">
            <Play className="w-4 h-4 text-kb-primary" fill="currentColor" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary">WATCH TUTORIAL</div>
            <div className="text-sm font-medium text-kb-ink mt-0.5 truncate">{t.title}</div>
            {t.description && <div className="text-xs text-kb-ink-muted mt-0.5 line-clamp-1">{t.description}</div>}
          </div>
        </button>
        {selected && <TutorialModal tutorial={selected} onClose={() => setSelected(null)} />}
      </>
    );
  }

  // Compact mode — pill button that opens a modal grid
  if (compact) {
    return (
      <>
        <button onClick={() => setOpen(true)} className="kb-mono text-[10px] uppercase tracking-wider px-3 py-2 rounded-sm border border-slate-200 hover:border-kb-primary text-kb-ink-muted hover:text-kb-primary inline-flex items-center gap-2" data-testid="tutorial-help-compact-btn">
          <Video className="w-3.5 h-3.5" /> {title}
        </button>
        {open && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 lg:p-8" onClick={() => setOpen(false)} data-testid="tutorial-help-modal">
            <div className="bg-white rounded-sm max-w-5xl w-full max-h-[88vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
                <div>
                  <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">HELP & TUTORIALS</div>
                  <h2 className="font-serif text-2xl text-kb-ink mt-1">Get the most out of <em className="italic text-kb-primary">KhojByte</em></h2>
                </div>
                <button onClick={() => setOpen(false)} className="p-2 text-kb-ink-muted hover:text-kb-primary"><X className="w-4 h-4" /></button>
              </div>
              <div className="p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {items.map((t) => <TutorialVideo key={t.key} tutorial={t} />)}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Inline grid — show all tutorials in this category
  return (
    <div data-testid="tutorial-help-grid">
      <div className="flex items-center justify-between mb-4">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">{title}</div>
        <Link to="/dashboard" className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary">VIEW ALL →</Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.slice(0, 3).map((t) => <TutorialVideo key={t.key} tutorial={t} compact />)}
      </div>
    </div>
  );
}

function TutorialModal({ tutorial, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="tutorial-single-modal">
      <div className="bg-white rounded-sm max-w-2xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="font-serif text-lg text-kb-ink">{tutorial.title}</h3>
          <button onClick={onClose} className="p-1 text-kb-ink-muted hover:text-kb-primary"><X className="w-4 h-4" /></button>
        </div>
        <TutorialVideo tutorial={tutorial} />
      </div>
    </div>
  );
}
